import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { getAreaOutlineForDisplay } from "./tableBookingUtils";

const ZOOM_MIN = 0.35;
const ZOOM_MAX = 4;
const ZOOM_STEP = 1.15;

const clampZoom = (z) =>
  Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(z * 1000) / 1000));

/**
 * Visual editor for normalized hotspots (0–1) on a scene image.
 * Renders zone markers plus optional per-table markers when a zone has nested tables.
 *
 * Guest-style flow: pass areaFirstNestedTables + revealNestedTablesAreaIndex (-1 = none)
 * and onMapZoneClick so table pins stay hidden until the zone is activated on the map.
 * Optional focusBounds per area draws the white frame and a large click target under pins.
 */
const SceneHotspotEditor = ({
  imageUrl,
  areas,
  focusIndex,
  focusTableIndex = null,
  onFocusTableIndex,
  onFocusIndex,
  onHotspotChange,
  onTableHotspotChange,
  addAreaOnClick = false,
  onAddAreaAtPoint,
  showLabels = true,
  showPrices = true,
  useStatusColors = true,
  showRadiusHandle = true,
  showCoordinateHud = false,
  compactChrome = false,
  showZoomControls = true,
  scrollZoom = true,
  areaFirstNestedTables = false,
  revealNestedTablesAreaIndex = -1,
  onMapZoneClick = undefined,
  outlineDrawMode = false,
  onOutlinePolygonChange,
  showAreaOutlines = true,
}) => {
  const { t } = useTranslation();
  const imgRef = useRef(null);
  const viewportRef = useRef(null);
  const dragTargetRef = useRef({ areaIndex: 0, tableIndex: null });
  const vertexDragRef = useRef({ areaIndex: -1, vertexIndex: -1 });
  const [dragging, setDragging] = useState(false);
  const [vertexDragging, setVertexDragging] = useState(false);
  const [radiusDrag, setRadiusDrag] = useState(false);
  const [imgNatural, setImgNatural] = useState({ w: 0, h: 0 });
  const [viewportSize, setViewportSize] = useState({ w: 0, h: 0 });
  const [zoom, setZoom] = useState(1);
  const [tooltipKey, setTooltipKey] = useState(null);
  const tipCloseTimerRef = useRef(null);

  const clearTipClose = useCallback(() => {
    if (tipCloseTimerRef.current) {
      window.clearTimeout(tipCloseTimerRef.current);
      tipCloseTimerRef.current = null;
    }
  }, []);

  const openTooltip = useCallback(
    (key) => {
      clearTipClose();
      setTooltipKey(key);
    },
    [clearTipClose]
  );

  const scheduleCloseTooltip = useCallback(() => {
    clearTipClose();
    tipCloseTimerRef.current = window.setTimeout(() => {
      setTooltipKey(null);
      tipCloseTimerRef.current = null;
    }, 140);
  }, [clearTipClose]);

  useEffect(() => () => clearTipClose(), [clearTipClose]);

  useEffect(() => {
    setZoom(1);
  }, [imageUrl]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (!cr) return;
      setViewportSize({ w: cr.width, h: cr.height });
    });
    ro.observe(el);
    setViewportSize({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, [imageUrl]);

  const layoutSize = useMemo(() => {
    if (!imgNatural.w || !imgNatural.h) {
      return { imgW: 0, imgH: 0 };
    }
    const padding = 8;
    const vw = viewportSize.w > 0 ? viewportSize.w : 520;
    const inner = Math.max(0, vw - padding * 2);
    const baseW =
      inner > 0 ? Math.min(900, Math.max(200, inner)) : Math.min(900, 520);
    const imgW = baseW * zoom;
    const imgH = (imgW * imgNatural.h) / imgNatural.w;
    return { imgW, imgH };
  }, [imgNatural, viewportSize.w, zoom]);

  const { imgW, imgH } = layoutSize;

  const displayRect = useMemo(
    () => ({
      ox: 0,
      oy: 0,
      w: imgW,
      h: imgH,
    }),
    [imgW, imgH]
  );

  const eventToNorm = useCallback((clientX, clientY) => {
    const img = imgRef.current;
    if (!img) return null;
    const rect = img.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    return {
      x: Math.min(1, Math.max(0, x / rect.width)),
      y: Math.min(1, Math.max(0, y / rect.height)),
    };
  }, []);

  const addOutlinePointAt = useCallback(
    (p) => {
      if (
        !outlineDrawMode ||
        !onOutlinePolygonChange ||
        focusIndex < 0 ||
        focusIndex >= areas.length
      ) {
        return;
      }
      const cur = getAreaOutlineForDisplay(areas[focusIndex]) || [];
      onOutlinePolygonChange(focusIndex, [...cur, p]);
    },
    [areas, focusIndex, onOutlinePolygonChange, outlineDrawMode]
  );

  const onOutlineDrawLayerMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const p = eventToNorm(e.clientX, e.clientY);
    if (!p) return;
    addOutlinePointAt(p);
  };

  useEffect(() => {
    const el = viewportRef.current;
    if (!el || !scrollZoom || !imageUrl?.trim()) return undefined;
    const fn = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const delta = e.deltaY > 0 ? -1 : 1;
      setZoom((z) =>
        clampZoom(delta < 0 ? z / ZOOM_STEP : z * ZOOM_STEP)
      );
    };
    el.addEventListener("wheel", fn, { passive: false });
    return () => el.removeEventListener("wheel", fn);
  }, [scrollZoom, imageUrl]);

  const applyPoint = useCallback(
    (p) => {
      const { areaIndex, tableIndex } = dragTargetRef.current;
      if (tableIndex != null && onTableHotspotChange) {
        const tbl = areas[areaIndex]?.tables?.[tableIndex];
        const h = tbl?.hotspot || {};
        onTableHotspotChange(areaIndex, tableIndex, {
          ...h,
          x: p.x,
          y: p.y,
        });
      } else {
        const h = areas[areaIndex]?.hotspot || {};
        onHotspotChange(areaIndex, {
          ...h,
          x: p.x,
          y: p.y,
        });
      }
    },
    [areas, onHotspotChange, onTableHotspotChange]
  );

  const onImageMouseDown = (e) => {
    if (!imageUrl) return;
    if (e.target !== imgRef.current) return;
    e.preventDefault();
    const p = eventToNorm(e.clientX, e.clientY);
    if (!p) return;

    if (addAreaOnClick && onAddAreaAtPoint) {
      onAddAreaAtPoint(p);
      return;
    }

    if (outlineDrawMode) return;

    if (focusIndex < 0 || focusIndex >= areas.length) return;
    dragTargetRef.current = {
      areaIndex: focusIndex,
      tableIndex: focusTableIndex != null ? focusTableIndex : null,
    };
    applyPoint(p);
    setDragging(true);
  };

  const onMouseMove = useCallback(
    (e) => {
      if (!dragging) return;
      const p = eventToNorm(e.clientX, e.clientY);
      if (!p) return;
      applyPoint(p);
    },
    [dragging, eventToNorm, applyPoint]
  );

  const onMouseUp = useCallback(() => {
    setDragging(false);
    setVertexDragging(false);
    setRadiusDrag(false);
  }, []);

  const patchOutlineVertex = useCallback(
    (areaIndex, vertexIndex, p) => {
      if (!onOutlinePolygonChange) return;
      const poly = getAreaOutlineForDisplay(areas[areaIndex]);
      if (!poly) return;
      const next = poly.map((pt, i) =>
        i === vertexIndex ? { x: p.x, y: p.y } : pt
      );
      onOutlinePolygonChange(areaIndex, next);
    },
    [areas, onOutlinePolygonChange]
  );

  const onVertexMouseDown = (e, areaIndex, vertexIndex) => {
    e.stopPropagation();
    e.preventDefault();
    onFocusIndex(areaIndex);
    if (onFocusTableIndex) onFocusTableIndex(null);
    vertexDragRef.current = { areaIndex, vertexIndex };
    setVertexDragging(true);
  };

  const removeOutlineVertex = (e, areaIndex, vertexIndex) => {
    e.stopPropagation();
    e.preventDefault();
    if (!onOutlinePolygonChange) return;
    const poly = getAreaOutlineForDisplay(areas[areaIndex]);
    if (!poly || poly.length <= 1) return;
    onOutlinePolygonChange(
      areaIndex,
      poly.filter((_, i) => i !== vertexIndex)
    );
  };

  useEffect(() => {
    if (!vertexDragging || !onOutlinePolygonChange) return undefined;
    const onMove = (e) => {
      const p = eventToNorm(e.clientX, e.clientY);
      if (!p) return;
      const { areaIndex, vertexIndex } = vertexDragRef.current;
      if (areaIndex < 0 || vertexIndex < 0) return;
      patchOutlineVertex(areaIndex, vertexIndex, p);
    };
    const onUp = () => setVertexDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [vertexDragging, eventToNorm, patchOutlineVertex, onOutlinePolygonChange]);

  useEffect(() => {
    if (!dragging) return undefined;
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [dragging, onMouseMove, onMouseUp]);

  useEffect(() => {
    if (!radiusDrag) return undefined;
    const onMove = (e) => {
      const p = eventToNorm(e.clientX, e.clientY);
      if (!p) return;
      const { areaIndex } = dragTargetRef.current;
      const ha = areas[areaIndex]?.hotspot || { x: 0.5, y: 0.5 };
      const dx = p.x - ha.x;
      const dy = p.y - ha.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const r = Math.min(0.45, Math.max(0.008, dist));
      onHotspotChange(areaIndex, { radius: r });
    };
    const onUp = () => setRadiusDrag(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [radiusDrag, areas, eventToNorm, onHotspotChange]);

  const onZoneDotMouseDown = (e, areaIndex) => {
    e.stopPropagation();
    e.preventDefault();
    onFocusIndex(areaIndex);
    if (onFocusTableIndex) onFocusTableIndex(null);
    onMapZoneClick?.(areaIndex);
    dragTargetRef.current = { areaIndex, tableIndex: null };
    setDragging(true);
  };

  const onTableDotMouseDown = (e, areaIndex, tableIndex) => {
    e.stopPropagation();
    e.preventDefault();
    onFocusIndex(areaIndex);
    if (onFocusTableIndex) onFocusTableIndex(tableIndex);
    dragTargetRef.current = { areaIndex, tableIndex };
    setDragging(true);
  };

  const onRadiusHandleDown = (e, areaIndex) => {
    e.stopPropagation();
    e.preventDefault();
    onFocusIndex(areaIndex);
    if (onFocusTableIndex) onFocusTableIndex(null);
    dragTargetRef.current = { areaIndex, tableIndex: null };
    setRadiusDrag(true);
  };

  if (!imageUrl?.trim()) {
    return (
      <div className="table-booking-hotspot-placeholder table-booking-studio-placeholder">
        {t("TABLE_BOOKING_SCENE_PREVIEW_HINT")}
      </div>
    );
  }

  const hudHotspot =
    focusIndex >= 0 &&
    focusIndex < areas.length &&
    focusTableIndex != null &&
    Array.isArray(areas[focusIndex]?.tables) &&
    areas[focusIndex].tables[focusTableIndex]
      ? areas[focusIndex].tables[focusTableIndex]?.hotspot || {}
      : focusIndex >= 0 && focusIndex < areas.length
        ? areas[focusIndex]?.hotspot || {}
        : null;

  const focusAreaOutline =
    focusIndex >= 0 && focusIndex < areas.length
      ? getAreaOutlineForDisplay(areas[focusIndex])
      : null;
  const hasCustomOutline =
    focusAreaOutline != null && focusAreaOutline.length >= 3;

  const showRadiusForZone =
    showRadiusHandle &&
    focusTableIndex == null &&
    focusIndex >= 0 &&
    focusIndex < areas.length &&
    !hasCustomOutline &&
    !outlineDrawMode;

  const renderAreaOutlineLayer = (a, ai) => {
    if (!showAreaOutlines) return null;
    const poly = getAreaOutlineForDisplay(a);
    if (!poly || poly.length < 1 || displayRect.w <= 0) return null;

    const isFocus = focusIndex === ai;
    const ptsStr = poly
      .map((p) => `${p.x * displayRect.w},${p.y * displayRect.h}`)
      .join(" ");
    const isClosed = poly.length >= 3;
    const tables = Array.isArray(a?.tables) ? a.tables : [];
    const hasTables = tables.length > 0;
    const showHit =
      areaFirstNestedTables &&
      hasTables &&
      revealNestedTablesAreaIndex !== ai;
    const showRing =
      isFocus &&
      (!areaFirstNestedTables || revealNestedTablesAreaIndex === ai);
    const canEditVertices = isFocus && Boolean(onOutlinePolygonChange);

    return (
      <svg
        key={`outline-${ai}`}
        className={`table-booking-area-outline-svg ${
          isFocus ? "is-focused" : "is-muted"
        } ${outlineDrawMode && isFocus ? "is-drawing" : ""}`}
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width: displayRect.w,
          height: displayRect.h,
          zIndex: isFocus ? (outlineDrawMode ? 10 : 2) : 1,
        }}
        viewBox={`0 0 ${displayRect.w} ${displayRect.h}`}
        aria-hidden={!isFocus}
      >
        {showHit && isClosed ? (
          <polygon
            points={ptsStr}
            className="table-booking-area-outline-hit"
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onFocusIndex(ai);
              if (onFocusTableIndex) onFocusTableIndex(null);
              onMapZoneClick?.(ai);
            }}
          />
        ) : null}
        {isClosed ? (
          <polygon
            points={ptsStr}
            className={`table-booking-area-outline-stroke ${
              showRing ? "is-ring" : ""
            }`}
          />
        ) : (
          <polyline
            points={ptsStr}
            className="table-booking-area-outline-stroke table-booking-area-outline-stroke--draft"
            fill="none"
          />
        )}
        {canEditVertices
          ? poly.map((p, vi) => (
              <circle
                key={`v-${ai}-${vi}`}
                cx={p.x * displayRect.w}
                cy={p.y * displayRect.h}
                r={outlineDrawMode ? 8 : 6}
                className="table-booking-area-outline-vertex"
                onMouseDown={(e) => onVertexMouseDown(e, ai, vi)}
                onDoubleClick={(e) => removeOutlineVertex(e, ai, vi)}
              />
            ))
          : null}
      </svg>
    );
  };

  const renderTableMarker = (a, ai, trow, ti) => {
    const h = trow?.hotspot || { x: 0.5, y: 0.5, radius: 0.02 };
    const cx = displayRect.ox + h.x * displayRect.w;
    const cy = displayRect.oy + h.y * displayRect.h;
    const radPx = Math.max(
      5,
      (h.radius || 0.02) * Math.min(displayRect.w, displayRect.h)
    );
    const zoneAvail = String(a?.manualAvailability || "AUTO").toUpperCase();
    const tblAvail = String(trow?.manualAvailability || "").toUpperCase();
    const avail = tblAvail || zoneAvail;
    let statusClass = "dot-neutral";
    if (useStatusColors) {
      statusClass =
        avail === "SOLD_OUT"
          ? "dot-sold-out"
          : avail === "UNAVAILABLE"
            ? "dot-unavailable"
            : "dot-auto";
    }
    const isFocus =
      focusIndex === ai && focusTableIndex === ti;
    const pinLine =
      trow?.priceDisplayLabel?.trim() ||
      (trow?.priceFromPerPerson !== "" &&
      trow?.priceFromPerPerson != null &&
      Number.isFinite(Number(trow.priceFromPerPerson))
        ? `${trow.currency || a.currency || "INR"} ${trow.priceFromPerPerson} / pax`
        : showPrices
          ? a?.priceDisplayLabel?.trim() ||
            `${a.currency || "INR"} ${a.priceFromPerPerson ?? ""} / pax`
          : "—");
    const tKey = `t-${ai}-${ti}`;
    const tipOpen = tooltipKey === tKey;
    const tableTitle =
      (trow?.label && String(trow.label).trim()) ||
      t("TABLE_BOOKING_TABLE_ROW", { n: ti + 1 });
    const nativeTitle = [
      showLabels && a?.label?.trim() ? `${a.label.trim()} · ${tableTitle}` : tableTitle,
      showPrices ? pinLine : null,
    ]
      .filter(Boolean)
      .join(" · ");

    return (
      <div
        key={`tm-${ai}-${ti}`}
        className={`table-booking-hotspot-marker table-booking-hotspot-marker--table ${isFocus ? "is-focus-marker" : ""}`}
        style={{ left: cx, top: cy }}
      >
        <div
          className="table-booking-hotspot-hit"
          onMouseEnter={() => openTooltip(tKey)}
          onMouseLeave={scheduleCloseTooltip}
        >
          {(showLabels || showPrices) && (
            <div
              className={`table-booking-hotspot-hover-card ${tipOpen ? "is-visible" : ""}`}
              role="tooltip"
              onMouseEnter={clearTipClose}
              onMouseLeave={scheduleCloseTooltip}
            >
              {showLabels ? (
                <div className="table-booking-hotspot-hover-card__title">
                  {a?.label?.trim() ? (
                    <>
                      <span className="table-booking-hotspot-hover-card__zone">
                        {a.label.trim()}
                      </span>
                      <span className="table-booking-hotspot-hover-card__sep"> · </span>
                    </>
                  ) : null}
                  {tableTitle}
                </div>
              ) : null}
              {showPrices ? (
                <div className="table-booking-hotspot-hover-card__meta">{pinLine}</div>
              ) : null}
            </div>
          )}
          <div
            className={`table-booking-hotspot-dot-wrap ${isFocus ? "is-selected-wrap" : ""}`}
            style={{ width: radPx * 2, height: radPx * 2 }}
          >
            <button
              type="button"
              className={`table-booking-hotspot-dot table-booking-hotspot-dot--table ${isFocus ? "is-focus" : ""} ${statusClass}`}
              style={{ width: "100%", height: "100%" }}
              title={nativeTitle}
              aria-label={nativeTitle}
              onMouseDown={(e) => onTableDotMouseDown(e, ai, ti)}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderZoneMarker = (a, ai) => {
    const h = a?.hotspot || { x: 0.5, y: 0.5, radius: 0.04 };
    const cx = displayRect.ox + h.x * displayRect.w;
    const cy = displayRect.oy + h.y * displayRect.h;
    const radPx = Math.max(
      6,
      (h.radius || 0.04) * Math.min(displayRect.w, displayRect.h)
    );
    const hasTables = Array.isArray(a?.tables) && a.tables.length > 0;
    const pinLine2 =
      a?.priceDisplayLabel?.trim() ||
      (Number(a?.priceFromPerPerson) || a?.priceFromPerPerson === 0
        ? `${a.currency || "INR"} ${a.priceFromPerPerson} / pax`
        : "—");
    const avail = String(a?.manualAvailability || "AUTO").toUpperCase();
    let statusClass = "dot-neutral";
    if (useStatusColors) {
      statusClass =
        avail === "SOLD_OUT"
          ? "dot-sold-out"
          : avail === "UNAVAILABLE"
            ? "dot-unavailable"
            : "dot-auto";
    }
    const isFocus = focusIndex === ai && focusTableIndex == null;
    const zKey = `z-${ai}`;
    const tipOpen = tooltipKey === zKey;
    const zoneTitle =
      a?.label?.trim() || t("TABLE_BOOKING_AREA_ROW", { n: ai + 1 });
    const nativeTitle = hasTables
      ? t("TABLE_BOOKING_ZONE_DOT_TITLE_NESTED")
      : [zoneTitle, showPrices ? pinLine2 : null].filter(Boolean).join(" · ");

    const showZoneHover =
      (showLabels || showPrices) && (hasTables ? showLabels : true);

    return (
      <div
        key={`zm-${ai}`}
        className={`table-booking-hotspot-marker table-booking-hotspot-marker--zone ${hasTables ? "has-nested-tables" : ""} ${isFocus ? "is-focus-marker" : ""}`}
        style={{ left: cx, top: cy }}
      >
        <div
          className="table-booking-hotspot-hit"
          onMouseEnter={() => openTooltip(zKey)}
          onMouseLeave={scheduleCloseTooltip}
        >
          {showZoneHover ? (
            <div
              className={`table-booking-hotspot-hover-card ${tipOpen ? "is-visible" : ""}`}
              role="tooltip"
              onMouseEnter={clearTipClose}
              onMouseLeave={scheduleCloseTooltip}
            >
              {showLabels ? (
                <div className="table-booking-hotspot-hover-card__title">
                  {hasTables ? (
                    <>
                      {zoneTitle}
                      <span className="table-booking-hotspot-hover-card__sep">
                        {" "}
                        ({t("TABLE_BOOKING_ZONE_LABEL")})
                      </span>
                    </>
                  ) : (
                    zoneTitle
                  )}
                </div>
              ) : null}
              {hasTables ? (
                showLabels ? (
                  <div className="table-booking-hotspot-hover-card__meta text-muted">
                    {t("TABLE_BOOKING_ZONE_PIN_SUB")}
                  </div>
                ) : null
              ) : showPrices ? (
                <div className="table-booking-hotspot-hover-card__meta">
                  {pinLine2}
                </div>
              ) : null}
            </div>
          ) : null}
          <div
            className={`table-booking-hotspot-dot-wrap ${isFocus ? "is-selected-wrap" : ""}`}
            style={{ width: radPx * 2, height: radPx * 2 }}
          >
            <button
              type="button"
              className={`table-booking-hotspot-dot table-booking-hotspot-dot--zone ${isFocus ? "is-focus" : ""} ${statusClass}`}
              style={{ width: "100%", height: "100%" }}
              title={nativeTitle}
              aria-label={nativeTitle}
              onMouseDown={(e) => onZoneDotMouseDown(e, ai)}
            />
            {isFocus && showRadiusForZone ? (
              <div
                className="table-booking-hotspot-radius-handle"
                aria-hidden
                onMouseDown={(e) => onRadiusHandleDown(e, ai)}
              />
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`table-booking-hotspot-editor ${compactChrome ? "table-booking-hotspot-editor--compact" : ""}`}
    >
      {!compactChrome ? (
        <p className="small text-muted mb-2">
          {addAreaOnClick
            ? t("TABLE_BOOKING_HOTSPOT_ADD_MODE_HELP")
            : outlineDrawMode
              ? t("TABLE_BOOKING_OUTLINE_DRAW_HINT")
              : t("TABLE_BOOKING_HOTSPOT_HELP")}
        </p>
      ) : null}
      {showZoomControls ? (
        <div className="table-booking-hotspot-zoom-bar">
          <button
            type="button"
            className="table-booking-studio-tool-btn"
            onClick={() => setZoom((z) => clampZoom(z / ZOOM_STEP))}
            disabled={zoom <= ZOOM_MIN + 0.001}
            aria-label={t("TABLE_BOOKING_ZOOM_OUT")}
            title={t("TABLE_BOOKING_ZOOM_OUT")}
          >
            −
          </button>
          <span className="table-booking-hotspot-zoom-pct">
            {t("TABLE_BOOKING_ZOOM_PCT", {
              pct: Math.round(zoom * 100),
            })}
          </span>
          <button
            type="button"
            className="table-booking-studio-tool-btn"
            onClick={() => setZoom((z) => clampZoom(z * ZOOM_STEP))}
            disabled={zoom >= ZOOM_MAX - 0.001}
            aria-label={t("TABLE_BOOKING_ZOOM_IN")}
            title={t("TABLE_BOOKING_ZOOM_IN")}
          >
            +
          </button>
          <button
            type="button"
            className="table-booking-studio-tool-btn"
            onClick={() => setZoom(1)}
            disabled={Math.abs(zoom - 1) < 0.001}
            title={t("TABLE_BOOKING_ZOOM_RESET")}
          >
            {t("TABLE_BOOKING_ZOOM_RESET")}
          </button>
          <span className="table-booking-hotspot-zoom-hint">
            {t("TABLE_BOOKING_ZOOM_HINT")}
          </span>
        </div>
      ) : null}
      <div ref={viewportRef} className="table-booking-hotspot-viewport">
        <div
          className={`table-booking-hotspot-canvas ${addAreaOnClick ? "table-booking-hotspot-canvas--pin-mode" : ""} ${outlineDrawMode ? "table-booking-hotspot-canvas--outline-draw" : ""} table-booking-studio-canvas`}
        >
          <img
            ref={imgRef}
            src={imageUrl}
            alt=""
            width={imgW || undefined}
            height={imgH || undefined}
            className="table-booking-scene-img table-booking-studio-scene-img"
            onLoad={(ev) => {
              const el = ev.target;
              setImgNatural({ w: el.naturalWidth, h: el.naturalHeight });
            }}
            onMouseDown={onImageMouseDown}
            draggable={false}
          />
          {showCoordinateHud && hudHotspot ? (
            <div className="table-booking-studio-coords-hud">
              X: {Number(hudHotspot.x ?? 0).toFixed(3)} · Y:{" "}
              {Number(hudHotspot.y ?? 0).toFixed(3)} · R:{" "}
              {Number(hudHotspot.radius ?? 0.04).toFixed(3)}
            </div>
          ) : null}
          {displayRect.w > 0 && !addAreaOnClick
            ? areas.map((a, ai) =>
                ai !== focusIndex ? renderAreaOutlineLayer(a, ai) : null
              )
            : null}
          {outlineDrawMode &&
          displayRect.w > 0 &&
          focusIndex >= 0 &&
          focusIndex < areas.length ? (
            <div
              className="table-booking-outline-draw-layer"
              style={{
                width: displayRect.w,
                height: displayRect.h,
              }}
              onMouseDown={onOutlineDrawLayerMouseDown}
              role="presentation"
            />
          ) : null}
          {displayRect.w > 0 &&
          !addAreaOnClick &&
          focusIndex >= 0 &&
          focusIndex < areas.length
            ? renderAreaOutlineLayer(areas[focusIndex], focusIndex)
            : null}
          {displayRect.w > 0 && !outlineDrawMode
            ? areas.flatMap((a, ai) => {
              const tables = Array.isArray(a?.tables) ? a.tables : [];
              const hasTables = tables.length > 0;
              const showTablePins =
                !areaFirstNestedTables ||
                !hasTables ||
                revealNestedTablesAreaIndex === ai;
              const tableMarkers = showTablePins
                ? tables.map((trow, ti) =>
                    renderTableMarker(a, ai, trow, ti)
                  )
                : [];
              const zoneMarker = renderZoneMarker(a, ai);
              return [...tableMarkers, zoneMarker];
            })
            : null}
        </div>
      </div>
    </div>
  );
};

SceneHotspotEditor.propTypes = {
  imageUrl: PropTypes.string,
  areas: PropTypes.arrayOf(PropTypes.object),
  focusIndex: PropTypes.number.isRequired,
  focusTableIndex: PropTypes.number,
  onFocusTableIndex: PropTypes.func,
  onFocusIndex: PropTypes.func.isRequired,
  onHotspotChange: PropTypes.func.isRequired,
  onTableHotspotChange: PropTypes.func,
  addAreaOnClick: PropTypes.bool,
  onAddAreaAtPoint: PropTypes.func,
  showLabels: PropTypes.bool,
  showPrices: PropTypes.bool,
  useStatusColors: PropTypes.bool,
  showRadiusHandle: PropTypes.bool,
  showCoordinateHud: PropTypes.bool,
  compactChrome: PropTypes.bool,
  showZoomControls: PropTypes.bool,
  scrollZoom: PropTypes.bool,
  areaFirstNestedTables: PropTypes.bool,
  revealNestedTablesAreaIndex: PropTypes.number,
  onMapZoneClick: PropTypes.func,
  outlineDrawMode: PropTypes.bool,
  onOutlinePolygonChange: PropTypes.func,
  showAreaOutlines: PropTypes.bool,
};

export default SceneHotspotEditor;
